/**
 * 現在地の取得、表示を行う
 */
import * as turf from '@turf/turf';
import config from '../config/config.js';

// configから設定を取得
const CURRENT_LOCATION_SETTINGS = config.currentLocation;
const GEOLOCATION_ERROR_MESSAGES = CURRENT_LOCATION_SETTINGS.messages.geolocation;
const NO_GEOLOCATION_MESSAGES = CURRENT_LOCATION_SETTINGS.messages.noGeolocation;

// マーカースタイル
const MS = CURRENT_LOCATION_SETTINGS.markerStyles;
const LOCATION_MARKER_STYLES = {
  FEEDBACK: {
    RADIUS: MS.feedback.radius,
    COLOR: MS.feedback.color,
    OPACITY: MS.feedback.opacity,
    STROKE_WIDTH: MS.feedback.strokeWidth,
    STROKE_COLOR: MS.feedback.strokeColor,
    STROKE_OPACITY: MS.feedback.strokeOpacity
  },
  CURRENT_LOCATION: {
    RADIUS: MS.currentLocation.radius,
    COLOR: MS.currentLocation.color,
    STROKE_WIDTH: MS.currentLocation.strokeWidth,
    STROKE_COLOR: MS.currentLocation.strokeColor,
    OPACITY: MS.currentLocation.opacity
  },
  ACCURACY: {
    COLOR: MS.accuracy.color,
    OPACITY: MS.accuracy.opacity
  }
};

// 状態定数
const BUTTON_STATES = {
  INACTIVE: 'inactive',
  LOCATING: 'locating',
  LOCATED: 'located'
};

// レイヤー/ソースID定数
const LAYER_IDS = {
  CURRENT_LOCATION: 'current-location-layer',
  CURRENT_LOCATION_ACCURACY: 'current-location-accuracy-layer',
  LOCATION_FEEDBACK: 'location-feedback-layer'
};
const SOURCE_IDS = {
  CURRENT_LOCATION: 'current-location',
  CURRENT_LOCATION_ACCURACY: 'current-location-accuracy',
  LOCATION_FEEDBACK: 'location-feedback'
};

// 共通処理：navigator.geolocation で現在地を取得する
function fetchCurrentPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      return resolve(null);
    }

    const options = {
      enableHighAccuracy: true,
      timeout: CURRENT_LOCATION_SETTINGS.buttonResetMs,
      // navigator.geolocation のオプション名は maximumAge（ms）
      maximumAge: CURRENT_LOCATION_SETTINGS.maximumAgeMs
    };

    navigator.geolocation.getCurrentPosition(
      pos => resolve({
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy
      }),
      err => resolve(null),
      options
    );
  });
}

// 現在地を取得するだけの関数（ハッシュなしでの初期表示時、現在地マーカー表示なし）
export function getCurrentLocationCoords() {
  return fetchCurrentPosition();
}

// 現在地を取得してマーカーを表示する関数（ボタン押下時）
export function getCurrentLocation(map, setCurrentLocationButtonState, setHasMapMoved) {
  if (!navigator.geolocation) {
    alert(NO_GEOLOCATION_MESSAGES);
    setCurrentLocationButtonState && setCurrentLocationButtonState(BUTTON_STATES.INACTIVE);
    return Promise.resolve(null);
  }

  // GPS測位中状態に変更
  setCurrentLocationButtonState && setCurrentLocationButtonState(BUTTON_STATES.LOCATING);

  return fetchCurrentPosition()
    .then((pos) => {
      if (!pos) {
        // 取得失敗時はボタン状態は戻す（UIが待機状態のままにならないように）
        setCurrentLocationButtonState && setCurrentLocationButtonState(BUTTON_STATES.INACTIVE);
        return null;
      }
      const { lat, lon, accuracy } = pos;

      if (map) {
        try {
          map.jumpTo(
            {
              center: [lon, lat],
              zoom: CURRENT_LOCATION_SETTINGS.targetZoom
            });
        } catch (e) {
        }
        try {
          addCurrentLocationMarker(map, lon, lat, accuracy);
        } catch (e) {
        }
      }

      // 現在地ボタン押下で移動時も、地図移動フラグtrue（再検索ボタン表示のため）
      setHasMapMoved(true);

      setCurrentLocationButtonState && setCurrentLocationButtonState(BUTTON_STATES.LOCATED);
      setTimeout(() => {
        setCurrentLocationButtonState && setCurrentLocationButtonState(BUTTON_STATES.INACTIVE);
      }, CURRENT_LOCATION_SETTINGS.buttonResetDelayMs);

      return { lat, lon, accuracy };
    })
    .catch((err) => {
      setCurrentLocationButtonState && setCurrentLocationButtonState(BUTTON_STATES.INACTIVE);
      if (err && err.code != null) {
        alert(GEOLOCATION_ERROR_MESSAGES[err.code]);
      }
      return Promise.resolve(null);
    });
}

// 現在地マーカーを地図に表示
function addCurrentLocationMarker(map, lon, lat, accuracy) {
  if (!map) return;

  // スタイル未ロードなら load イベントで遅延実行
  if (!map.isStyleLoaded()) {
    map.once('load', () => addCurrentLocationMarker(map, lon, lat, accuracy));
    return;
  }

  // 既存のマーカーを削除
  if (map.getSource(SOURCE_IDS.CURRENT_LOCATION)) {
    map.removeLayer(LAYER_IDS.CURRENT_LOCATION);
    map.removeSource(SOURCE_IDS.CURRENT_LOCATION);
  }

  if (map.getSource(SOURCE_IDS.CURRENT_LOCATION_ACCURACY)) {
    map.removeLayer(LAYER_IDS.CURRENT_LOCATION_ACCURACY);
    map.removeSource(SOURCE_IDS.CURRENT_LOCATION_ACCURACY);
  }

  // 測位完了フィードバック用のグレー円を削除（既存があれば）
  if (map.getSource(SOURCE_IDS.LOCATION_FEEDBACK)) {
    map.removeLayer(LAYER_IDS.LOCATION_FEEDBACK);
    map.removeSource(SOURCE_IDS.LOCATION_FEEDBACK);
  }

  // 精度が低い場合は精度円を表示
  if (accuracy && accuracy > CURRENT_LOCATION_SETTINGS.accuracyThreshold) {
    // turf:JavaScriptの地理空間解析ライブラリ
    // 現在地の緯度経度（[lon, lat]）を中心に、精度（accuracy）メートルの半径を持つ円（GeoJSON形式）を生成する処理
    const accuracyCircle = turf.circle([lon, lat], accuracy, { units: 'meters' });

    // 現在地の精度を示す円を地図上に描画するためのデータソースを追加
    map.addSource(SOURCE_IDS.CURRENT_LOCATION_ACCURACY, {
      type: 'geojson',
      data: accuracyCircle
    });

    // 精度円を地図上に描画
    map.addLayer({
      id: LAYER_IDS.CURRENT_LOCATION_ACCURACY,
      type: 'fill',
      source: SOURCE_IDS.CURRENT_LOCATION_ACCURACY,
      paint: {
        'fill-color': LOCATION_MARKER_STYLES.ACCURACY.COLOR,
        'fill-opacity': LOCATION_MARKER_STYLES.ACCURACY.OPACITY
      }
    });
  }

  // 測位完了フィードバック用の大きなグレー透明円のデータソースを追加（青丸の下のレイヤー）
  map.addSource(SOURCE_IDS.LOCATION_FEEDBACK, {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat]
      }
    }
  });

  // フィードバック円を地図上に描画
  map.addLayer({
    id: LAYER_IDS.LOCATION_FEEDBACK,
    type: 'circle',
    source: SOURCE_IDS.LOCATION_FEEDBACK,
    paint: {
      'circle-radius': LOCATION_MARKER_STYLES.FEEDBACK.RADIUS,
      'circle-color': LOCATION_MARKER_STYLES.FEEDBACK.COLOR,
      'circle-opacity': LOCATION_MARKER_STYLES.FEEDBACK.OPACITY,
      'circle-stroke-width': LOCATION_MARKER_STYLES.FEEDBACK.STROKE_WIDTH,
      'circle-stroke-color': LOCATION_MARKER_STYLES.FEEDBACK.STROKE_COLOR,
      'circle-stroke-opacity': LOCATION_MARKER_STYLES.FEEDBACK.STROKE_OPACITY
    }
  });

  // 現在地を示すマーカーのデータソースを追加
  map.addSource(SOURCE_IDS.CURRENT_LOCATION, {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat]
      }
    }
  });

  // 現在地マーカーを表示（青丸が上のレイヤーになる）
  map.addLayer({
    id: LAYER_IDS.CURRENT_LOCATION,
    type: 'circle',
    source: SOURCE_IDS.CURRENT_LOCATION,
    paint: {
      'circle-radius': LOCATION_MARKER_STYLES.CURRENT_LOCATION.RADIUS,
      'circle-color': LOCATION_MARKER_STYLES.CURRENT_LOCATION.COLOR,
      'circle-stroke-width': LOCATION_MARKER_STYLES.CURRENT_LOCATION.STROKE_WIDTH,
      'circle-stroke-color': LOCATION_MARKER_STYLES.CURRENT_LOCATION.STROKE_COLOR,
      'circle-opacity': LOCATION_MARKER_STYLES.CURRENT_LOCATION.OPACITY,
    }
  });

  // 2秒後にグレー円をフェードアウトして削除
  setTimeout(() => {
    if (map.getLayer(LAYER_IDS.LOCATION_FEEDBACK)) {
      // フェードアウトアニメーション
      let opacity = CURRENT_LOCATION_SETTINGS.feedbackInitialOpacity;
      let strokeOpacity = CURRENT_LOCATION_SETTINGS.feedbackInitialStrokeOpacity;

      const fadeOut = setInterval(() => {
        opacity -= CURRENT_LOCATION_SETTINGS.feedbackOpacityDecrement;
        strokeOpacity -= CURRENT_LOCATION_SETTINGS.feedbackStrokeOpacityDecrement;

        if (opacity <= 0) {
          // 完全に透明になったら削除
          if (map.getSource(SOURCE_IDS.LOCATION_FEEDBACK)) {
            map.removeLayer(LAYER_IDS.LOCATION_FEEDBACK);
            map.removeSource(SOURCE_IDS.LOCATION_FEEDBACK);
          }
          // 精度円も同様に削除
          if (map.getSource(SOURCE_IDS.CURRENT_LOCATION_ACCURACY)) {
            map.removeLayer(LAYER_IDS.CURRENT_LOCATION_ACCURACY);
            map.removeSource(SOURCE_IDS.CURRENT_LOCATION_ACCURACY);
          }

          clearInterval(fadeOut);
        } else {
          // 透明度を更新
          if (map.getLayer(LAYER_IDS.LOCATION_FEEDBACK)) {
            map.setPaintProperty(LAYER_IDS.LOCATION_FEEDBACK, 'circle-opacity', opacity);
            map.setPaintProperty(LAYER_IDS.LOCATION_FEEDBACK, 'circle-stroke-opacity', strokeOpacity);
          }
        }
      }, CURRENT_LOCATION_SETTINGS.feedbackFadeoutIntervalMs); // nミリ秒ごとに透明度を下げる
    }
  }, CURRENT_LOCATION_SETTINGS.feedbackFadeoutDelayMs); // nミリ秒後に開始
}



